import { CurrenciesResponse, Currency } from '@/types/sso';
import { useMemo } from 'react';
import { useSso } from '../providers/SSOProvider';
import { getCurrency } from './useCurrency';

export interface CurrencyOption {
    label: string;
    name: string;
    value: number;
}

export interface UseCurrencyOptionsReturn {
    currenciesData: Currency[];
    isCurrenciesLoading: boolean;
    cryptoDepositCcyOptions: CurrencyOption[];
    cryptoWithdrawalCcyOptions: CurrencyOption[];
    fiatDepositCcyOptions: CurrencyOption[];
    fiatWithdrawalCcyOptions: CurrencyOption[];
    transferCcyOptions: CurrencyOption[];
}

const transformToOutput = (currencies: Currency[]) => {
    return currencies.map((currency) => {
        const { show } = getCurrency(currency.assetName || currency.code);

        return {
            label: show,
            name: show,
            value: currency.id
        };
    });
};

const useCurrencyOptions = (selectedTransferType?): UseCurrencyOptionsReturn => {
    const { instrumentCurrenciesApi } = useSso();
    const { data: instrumentCurrenciesData = [], isLoading: isLoadingInstrumentCurrencies } = instrumentCurrenciesApi;

    const currenciesData = useMemo(() => instrumentCurrenciesData || [], [instrumentCurrenciesData]);

    const cryptoDepositCcyOptions = useMemo(() => {
        return transformToOutput(
            currenciesData.filter((currency) => currency.allowDepositWithdrawal && currency.isCrypto)
        );
    }, [currenciesData]);

    const cryptoWithdrawalCcyOptions = useMemo(() => {
        return transformToOutput(
            currenciesData.filter((currency) => currency.allowDepositWithdrawal && currency.isCrypto)
        );
    }, [currenciesData]);

    const fiatDepositCcyOptions = useMemo(() => {
        return transformToOutput(
            currenciesData.filter((currency) => currency.allowDepositWithdrawal && !currency.isCrypto)
        );
    }, [currenciesData]);

    const fiatWithdrawalCcyOptions = useMemo(() => {
        return transformToOutput(
            currenciesData.filter((currency) => currency.allowDepositWithdrawal && !currency.isCrypto)
        );
    }, [currenciesData]);

    const transferCcyOptions = useMemo(() => {
        return transformToOutput(
            currenciesData.filter(
                (currency) =>
                    currency.allowDepositWithdrawal &&
                    (selectedTransferType?.value === 'FIAT' ? !currency.isCrypto : currency.isCrypto)
            )
        );
    }, [currenciesData, selectedTransferType]);

    return {
        currenciesData,
        isCurrenciesLoading: isLoadingInstrumentCurrencies,
        cryptoDepositCcyOptions,
        cryptoWithdrawalCcyOptions,
        fiatDepositCcyOptions,
        fiatWithdrawalCcyOptions,
        transferCcyOptions
    };
};

export default useCurrencyOptions;
